import { useCallback, useContext, useEffect } from 'react'
import toast from 'react-hot-toast'

import {
  AccountContext,
  AccountCtx,
  ConnectAsFn,
  UpdateProfileFn,
} from 'src/components/data/providers/AccountProvider'
import useProductPage from 'src/hooks/useProductPage'
import useSettings from 'src/hooks/useSettings'
import useWizard from 'src/hooks/useWizard'
import { sendSentryErrorFn, setSentryUserFn } from 'src/utils/sentryUtils'

const useAccount = (): AccountCtx & {
  changeSeller: ConnectAsFn
  changeProfile: UpdateProfileFn
} => {
  const context = useContext(AccountContext)
  const {
    connectAs: connectAsContext,
    updateProfile: updateProfileContext,
    getCurrentUser,
    setLoadingSeller,
  } = context

  const { getComponents, getCurrencies, getDefaultDashboardToLoad } = useSettings()
  const { getDashboard: getDashboardWizard } = useWizard()
  const { code, program } = useProductPage()

  const refreshSettings = useCallback(async () => {
    await getCurrentUser()

    const promises = await Promise.all([
      getComponents(),
      getDashboardWizard().catch(e => sendSentryErrorFn(e)),
      getCurrencies(),
    ])

    const [menus] = promises

    const favoriteSettings = await getDefaultDashboardToLoad({
      subProduct: code,
      enableLoading: true,
      resetSettings: true,
      programId: program,
      menus,
    })

    return [...promises, favoriteSettings]
  }, [
    code,
    getComponents,
    getCurrencies,
    getCurrentUser,
    getDashboardWizard,
    getDefaultDashboardToLoad,
  ])

  const changeSeller: ConnectAsFn = async (sellerId: string, cb?: () => void) => {
    setLoadingSeller(true)
    cb?.()
    await connectAsContext(sellerId)
    await refreshSettings()
    setLoadingSeller(false)
    toast.success('Company updated')
  }

  const changeProfile: UpdateProfileFn = async (sellerId: string, cb?: () => void) => {
    setLoadingSeller(true)
    cb?.()
    await updateProfileContext(sellerId)
    await refreshSettings()
    setLoadingSeller(false)
    toast.success('Profile updated')
  }

  // Set sentry user
  const user = context?.informations
  useEffect(() => {
    setSentryUserFn(user)
  }, [user])

  return { ...context, changeSeller, changeProfile }
}

export default useAccount
